import React, { useState, useEffect, useRef } from 'react';
import { fetchVenueIntegration, generateVenueSecret } from "../../../services/Http/Api";
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';

const VenueIntegration = ({ venueId }) => {
    const { t } = useTranslation();
    const [apiKey, setApiKey] = useState('');
    const [apiSecret, setApiSecret] = useState(null);
    const [secretTimer, setSecretTimer] = useState(60);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(false);
    const venueRequest = useRef(0);
    const generatingRequest = useRef(null);

    useEffect(() => {
        const request = ++venueRequest.current;
        generatingRequest.current = null;
        setApiKey('');
        setApiSecret(null);
        setGenerating(false);
        setLoading(true);
        setError(false);
        const loadVenueIntegration = async () => {
            try {
                const response = await fetchVenueIntegration(venueId);
                if (request !== venueRequest.current) return;
                setApiKey(response.api_key || '');
            } catch (error) {
                if (request === venueRequest.current) setError(true);
            } finally {
                if (request === venueRequest.current) setLoading(false);
            }
        };

        if (venueId) loadVenueIntegration();
        else setLoading(false);
        return () => { venueRequest.current += 1; };
    }, [venueId]);

    useEffect(() => {
        if (!apiSecret) return;
        const countdown = setInterval(() => setSecretTimer((seconds) => Math.max(0, seconds - 1)), 1000);
        const expiry = setTimeout(() => setApiSecret(null), 60000);
        return () => {
            clearInterval(countdown);
            clearTimeout(expiry);
        };
    }, [apiSecret]);

    const handleGenerateSecret = async () => {
        if (!venueId || loading || generatingRequest.current !== null) return;
        const request = venueRequest.current;
        generatingRequest.current = request;
        setGenerating(true);
        setApiSecret(null);
        setError(false);
        try {
            const response = await generateVenueSecret(venueId);
            if (request !== venueRequest.current) return;
            setApiKey(response.api_key);
            setApiSecret(response.api_secret);
            setSecretTimer(60);
        } catch (error) {
            if (request === venueRequest.current) setError(true);
        } finally {
            if (request === venueRequest.current) {
                generatingRequest.current = null;
                setGenerating(false);
            }
        }
    };

    return (
        <PageContent>
            <StyledCard>
                <IntegrationHeader>
                    <Title>{t('Venues.VenueView.VenueIntegration.api_integration')}</Title>
                </IntegrationHeader>

                <IntegrationBody>
                    {loading && <p>{t('Settings.loading')}</p>}
                    {error && <p role="alert">{t('Settings.Integrations.error')}</p>}
                    <FormGroup>
                        <Label htmlFor="apiKeyInput">
                            {t('Venues.VenueView.VenueIntegration.api_key')}
                        </Label>
                        <Input type="text" id="apiKeyInput" value={apiKey} readOnly />
                    </FormGroup>

                    <div style={{ marginTop: '10px' }}>
                        <MainButton onClick={handleGenerateSecret} size="small" disabled={!venueId || loading || generating}>
                            {t('Venues.VenueView.VenueIntegration.generate_secret')}
                        </MainButton>
                    </div>

                    {apiSecret && (
                        <SecretContainer>
                            <Alert>
                                <strong>
                                    {t('Venues.VenueView.VenueIntegration.generated_secret', { secret: apiSecret })}
                                </strong>
                            </Alert>
                            <Timer>
                                {t('Venues.VenueView.VenueIntegration.secret_timer', { seconds: secretTimer })}
                            </Timer>
                        </SecretContainer>
                    )}
                </IntegrationBody>
            </StyledCard>
        </PageContent>
    );
};

export default VenueIntegration;

// Styled Components

const IntegrationHeader = styled.div`
    border-bottom: 2px solid #f1f5f9;
    padding-bottom: 1rem;
    margin-bottom: 1.5rem;
`;

const Title = styled.h3`
    color: #1e293b;
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
`;

const IntegrationBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
`;

const Input = styled.input`
    padding: 0.75rem 1rem;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-family: inherit;
    font-size: 0.95rem;
    color: #1e293b;
    width: 100%;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #701a82;
        background: #fff;
        box-shadow: 0 0 0 4px rgba(112, 26, 130, 0.1);
    }

    &[readOnly] {
        cursor: default;
        background: #f1f5f9;
    }
`;

const SecretContainer = styled.div`
    margin-top: 0.5rem;
    padding: 1.25rem;
    background: #f0fdf4;
    border: 1.5px solid #bbf7d0;
    border-radius: 12px;
    animation: fadeIn 0.3s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

const Alert = styled.div`
    font-size: 0.95rem;
    color: #166534;
    line-height: 1.6;
    word-break: break-all;

    strong {
        font-weight: 700;
        color: #15803d;
    }
`;

const Timer = styled.p`
    font-size: 0.875rem;
    color: #166534;
    margin: 0.75rem 0 0;
    font-weight: 600;
    opacity: 0.8;
`;
